// `/amplemarket/callback?code=…&state=…` — where Amplemarket's OAuth consent returns (GRO-287).
//
// The page is behind the Clerk gate like every route, so it POSTs the code and state with the
// signed-in person's session. The server completes the connect only when that same person
// started it, so a forwarded callback link connects nobody.

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { completeSequenceConnection, panelMessage } from '../api/client';
import { useAuthContext } from '../auth/AuthContext';
import { Gate } from '../auth/Gate';

export function AmplemarketCallbackView() {
  const { getToken } = useAuthContext();
  const [searchParams] = useSearchParams();
  // Read once: the address bar is cleared below, and the page must keep what it arrived with.
  const [code] = useState(() => searchParams.get('code') || '');
  const [state] = useState(() => searchParams.get('state') || '');
  const [denied] = useState(() => searchParams.get('error'));
  const [result, setResult] = useState<'working' | 'connected' | 'failed'>('working');
  const [errorText, setErrorText] = useState('');
  const sent = useRef(false);
  const navigate = useNavigate();

  // The code is single-use, so it is posted once even under React's double effect in dev. The
  // code and state then leave the address bar (and history), so neither sits in a URL afterwards.
  useEffect(() => {
    if (sent.current) return;
    if (code || state || denied) navigate('/amplemarket/callback', { replace: true });
    if (denied || !code || !state) return;
    sent.current = true;
    completeSequenceConnection(getToken, { code, state })
      .then(() => setResult('connected'))
      .catch(error => { setErrorText(panelMessage(error, 'Could not connect Amplemarket.')); setResult('failed'); });
  }, [code, state, denied, getToken, navigate]);

  if (denied) return <Gate message="Amplemarket was not connected."><BackLink /></Gate>;
  if (!code || !state) return <Gate message="This page needs the link Amplemarket sends back."><BackLink /></Gate>;
  if (result === 'working') return <div className="loading">Connecting Amplemarket…</div>;
  if (result === 'failed') return <Gate message={errorText}><BackLink /></Gate>;
  return (
    <Gate message="Amplemarket is connected.">
      <p className="denied-who">Open an account and use its Sequence tab.</p>
      <BackLink />
    </Gate>
  );
}

function BackLink() {
  return <p className="denied-who"><Link to="/">Back to the dashboard</Link></p>;
}
